import { useEffect, useRef, useState } from "react";
import Sidebar from "../common/Sidebar";
import "../customCss/AllUsers.css";
import DataService from "../services/data.service";
import LoadingBar from "react-top-loading-bar";
import { toast } from "react-toastify";
import Logout from "../common/Logout";
import moment from "moment";
import { TextField } from "@mui/material";
import { MdAdd } from "react-icons/md";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const AllUsers = () => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [newPopup, setNewPopup] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Add/Edit States
  const [isEditing, setIsEditing] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [kids, setKids] = useState([{ name: "", age: "", birthDate: null }]);

  const resetFields = () => {
    setParentName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setZip("");
    setKids([{ name: "", age: "", birthDate: null }]);
    setIsEditing(false);
    setEditId(null);
  };

  const getAllUsers = async () => {
    setLoading(true);
    ref.current.continuousStart();
    try {
      const res = await DataService.getAllUsers();
      const data = res?.data?.data || [];
      setUsers(data.reverse());
      setFiltered(data.reverse());
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
      ref.current.complete();
    }
  };

  useEffect(() => {
    const filterUsers = () => {
      if (!users || users.length === 0) return;
      if (!searchName && !searchDate) {
        setFiltered(users);
        return;
      }

      const filteredData = users.filter((b) => {
        const nameMatch = searchName
          ? b?.parentName?.toLowerCase()?.includes(searchName.toLowerCase())
          : true;

        const dateMatch = searchDate
          ? b?.createdAt?.startsWith(searchDate) ||
            b?.bookingDate?.startsWith(searchDate)
          : true;

        return nameMatch && dateMatch;
      });

      setFiltered(filteredData);
    };

    filterUsers();
  }, [searchName, searchDate, users]);

  useEffect(() => {
    document.title = "All Users";
    getAllUsers();
  }, []);

  const handleKidChange = (index, field, value) => {
    const newKids = [...kids];
    newKids[index][field] = value;
    setKids(newKids);
  };

  const addKid = () => {
    setKids([...kids, { name: "", age: "", birthDate: null }]);
  };

  const removeKid = (index) => {
    const newKids = kids.filter((_, i) => i !== index);
    setKids(newKids);
  };

  const handleSubmit = async () => {
    if (!parentName || !email || !phone || !address || !zip) {
      toast.error("Please fill all required fields");
      return;
    }
    for (let k of kids) {
      if (!k.name || !k.age || !k.birthDate) {
        toast.error("All kid fields are required");
        return;
      }

      if (k.age <= 0) {
        toast.error("Age must be greater than 0");
        return;
      }
    }

    const payload = {
      parentName,
      email,
      phoneNo: phone,
      address,
      zipCode: zip,
      password: "StrongPass123",
      kids: kids.map((k) => ({
        ...k,
        age: Number(k.age),
        birthDate: k.birthDate ? new Date(k.birthDate).toISOString() : null,
      })),
    };
    setButtonLoading(true);
    try {
      if (isEditing && editId) {
        await DataService.updateUser(editId, payload);
        toast.success("User updated successfully");
      } else {
        await DataService.createUser(payload);
        toast.success("User added successfully");
      }
      setNewPopup(false);
      resetFields();
      getAllUsers();
    } catch (error) {
      console.error(error);
      toast.error("Action failed");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleEdit = (user) => {
    setIsEditing(true);
    setEditId(user._id);
    setParentName(user?.parentName || "");
    setEmail(user?.email || "");
    setPhone(user?.phoneNo || "");
    setAddress(user?.address || "");
    setZip(user?.zipCode || "");
    setKids(
      user?.kids?.length
        ? user.kids.map((k) => ({
            name: k.name || "",
            age: k.age || "",
            birthDate: k.birthDate ? dayjs(new Date(k.birthDate)) : null,
          }))
        : [{ name: "", age: "", birthDate: null }]
    );
    setNewPopup(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeletePopup(true);
  };

  const handleDelete = async () => {
    if (!selectedUser?._id) return;
    setLoading(true);
    try {
      await DataService.deleteUser(selectedUser._id);
      toast.success("User deleted successfully");
      setDeletePopup(false);
      setSelectedUser(null);
      getAllUsers();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main_section">
      <div className="main_flex">
        <div className="main_flexL">
          <Sidebar />
        </div>
        <div className="main_flexR">
          <div className="right_title">
            <h1>All Users</h1>
            <Logout />
          </div>

          <LoadingBar color="#84C342" ref={ref} height={5} />
          <div className="filters_sec">
            <div className="filtersL">
              <div className="row">
                <div className="col-sm-3">
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      placeholder="Search by Parent Name"
                      value={searchName}
                      className="form-control"
                      id="floatingInputParent"
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                    <label htmlFor="floatingInputParent">
                      Search by Parent Name
                    </label>
                  </div>
                </div>
                <div className="col-sm-3">
                  <div className="form-floating mb-3">
                    <input
                      type="date"
                      value={searchDate}
                      placeholder="Created at Date"
                      className="form-control"
                      id="floatingInputDate"
                      onChange={(e) => setSearchDate(e.target.value)}
                    />
                    <label htmlFor="floatingInputDate">Created at Date</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="filtersR text-end">
              <button id="reloadBtn" onClick={getAllUsers} disabled={loading}>
                <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>{" "}
                Reload
              </button>
              <button
                className="btn btn-primary ms-3"
                onClick={() => setNewPopup(true)}
              >
                <i className="fas fa-plus" /> Add New
              </button>
            </div>
          </div>

          <div className="main_innerSec">
            <div className="main_booksSec">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th>#</th>
                    <th>Parent Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>ZIP</th>
                    <th>Kids</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9">Loading users...</td>
                    </tr>
                  ) : filtered?.length > 0 ? (
                    filtered.map((user, index) => (
                      <tr key={user._id || index}>
                        <td>{index + 1}</td>
                        <td>{user?.parentName || "-"}</td>
                        <td>{user?.email || "-"}</td>
                        <td>{user?.phoneNo || "-"}</td>
                        <td>{user?.address || "-"}</td>
                        <td>{user?.zipCode || "-"}</td>
                        <td>
                          {user?.kids?.length
                            ? user.kids
                                .map(
                                  (k) =>
                                    `${k.name} (${k.age} yrs, ${
                                      k.birthDate
                                        ? moment(k.birthDate).format(
                                            "DD MMM YYYY"
                                          )
                                        : "-"
                                    })`
                                )
                                .join(", ")
                            : "-"}
                        </td>
                        <td>
                          {moment(user?.createdAt).format("MM/DD/YYYY hh:mm A")}
                        </td>
                        <td className="text-center">
                          <i
                            className="fas fa-edit text-primary me-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEdit(user)}
                          />
                          <i
                            className="fas fa-trash text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteClick(user)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Add / Edit Popup ===== */}
      {newPopup && (
        <div className="popOuter">
          <div className="popInner">
            <i
              className="fas fa-times"
              onClick={() => {
                setNewPopup(false);
                resetFields();
              }}
            />
            <div className="popBody">
              <div className="popForm">
                <h2 className="mb-3">
                  {isEditing ? "Edit User" : "Add New User"}
                </h2>

                <div className="row g-3">
                  <div className="col-sm-12">
                    <TextField
                      label="Parent Name"
                      value={parentName}
                      onChange={(e) => setParentName(e.target.value)}
                      fullWidth
                      required
                    />
                  </div>
                  <div className="col-sm-6">
                    <TextField
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                    />
                  </div>
                  <div className="col-sm-6">
                    <TextField
                      label="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fullWidth
                      required
                    />
                  </div>
                  <div className="col-sm-12">
                    <TextField
                      label="Address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      fullWidth
                      required
                    />
                  </div>
                  <div className="col-sm-12">
                    <TextField
                      label="ZIP Code"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      fullWidth
                      required
                    />
                  </div>
                </div>

                <h5 className="mt-4">Kids *</h5>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {kids?.map((kid, index) => (
                    <div className="row g-3 mb-3 position-relative" key={index}>
                      <div className="col-sm-4">
                        <TextField
                          label={`Kid ${index + 1} Name`}
                          value={kid.name}
                          onChange={(e) =>
                            handleKidChange(index, "name", e.target.value)
                          }
                          fullWidth
                          required
                        />
                      </div>
                      <div className="col-sm-3">
                        <TextField
                          label="Age"
                          type="number"
                          value={kid.age}
                          onChange={(e) =>
                            handleKidChange(index, "age", e.target.value)
                          }
                          fullWidth
                          required
                        />
                      </div>
                      <div className="col-sm-4">
                        <DatePicker
                          label="Birth Date"
                          value={kid.birthDate}
                          disableFuture
                          onChange={(newValue) =>
                            handleKidChange(index, "birthDate", newValue)
                          }
                          slotProps={{
                            textField: { fullWidth: true, required: true },
                          }}
                        />
                      </div>
                      {kids.length > 1 && (
                        <i
                          className="fas fa-times"
                          onClick={() => removeKid(index)}
                        />
                      )}
                    </div>
                  ))}
                </LocalizationProvider>

                <div className="text-center mb-3">
                  <button className="addField" onClick={addKid}>
                    <MdAdd />
                  </button>
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-primary"
                    disabled={buttonLoading}
                    onClick={handleSubmit}
                    style={{ color: "#fff" }}
                  >
                    {buttonLoading ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : isEditing ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Delete Confirmation Popup ===== */}
      {deletePopup && (
        <div className="popOuter">
          <div
            className="popInner"
            style={{ maxWidth: "500px", minWidth: "500px" }}
          >
            <i
              className="fas fa-times"
              onClick={() => {
                setDeletePopup(false);
                setSelectedUser(null);
              }}
            />
            <div className="popBody">
              <h4>Delete User?</h4>
              <p className="mb-0 fw-bold">{selectedUser?.parentName}</p>
              <p style={{ fontSize: "15px", marginBottom: "15px" }}>
                {selectedUser?.email}
              </p>
              <div className="text-center">
                <button
                  className="btn btn_danger btn-primary py-2 me-2"
                  disabled={loading}
                  onClick={handleDelete}
                >
                  {loading ? (
                    <div className="spinner-border button mx-4" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
                <button
                  className="btn btn-primary py-2"
                  onClick={() => {
                    setDeletePopup(false);
                    setSelectedUser(null);
                  }}
                >
                  No, Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsers;
